import React, { useMemo, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { Button } from 'src/components/ui/button';
import { Calendar } from 'src/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { useTranslation } from 'react-i18next';

const getDefaultMonth = (selected: unknown): Date => {
  if (selected instanceof Date) return selected;

  if (
    selected &&
    typeof selected === 'object' &&
    'from' in selected &&
    (selected as { from?: unknown }).from instanceof Date
  ) {
    return (selected as { from: Date }).from;
  }

  if (Array.isArray(selected) && selected[0] instanceof Date) {
    return selected[0];
  }

  return new Date();
};

export const DatePicker: React.FC<
  React.ComponentPropsWithoutRef<typeof Calendar> & {
    children?: React.ReactNode;
    buttonProps?: React.ComponentPropsWithoutRef<typeof Button>;
    dateFormatString?: string;
    dateFormatParams?: {
      val: Record<string, any>;
    };
    placeholder?: string;
  }
> = ({
  children,
  buttonProps,
  dateFormatString = '{{val, datetime}}',
  dateFormatParams,
  placeholder,
  ...props
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Key to remount Calendar when popover opens (resets to default month)
  const [calendarKey, setCalendarKey] = useState(0);

  // Calculate default month from selected value
  const defaultMonth = useMemo(() => getDefaultMonth(props.selected), [props.selected]);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setCalendarKey((k) => k + 1);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          {...buttonProps}
          variant={'outline'}
          className={cn(
            'tw-h-11 tw-w-[240px] tw-justify-start tw-text-left tw-font-normal',
            !props.selected && 'tw-text-muted-foreground',
            buttonProps?.className,
          )}
        >
          <CalendarIcon size={20} className={'tw-me-3'} />
          {children ||
            (props.selected ? (
              t(dateFormatString, {
                val: props.selected,
                formatParams: dateFormatParams,
              })
            ) : (
              <span className={'tw-text-text-inactive'}>{placeholder ?? t('Pick a date')}</span>
            ))}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={'tw-z-[1100] tw-w-auto tw-p-0'}
        align={'start'}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar key={calendarKey} {...props} defaultMonth={defaultMonth} />
      </PopoverContent>
    </Popover>
  );
};
